/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { Menu } from "primereact/menu";
import { Tag } from "primereact/tag";
import { confirmDialog } from "primereact/confirmdialog";
import { ConfirmDialog } from "primereact/confirmdialog";
import {
  Boton,
  Titulo,
  Formulario,
  accessAPI,
  Sidebar,
  TablaFiltrable,
  useFuncionalidad,
} from "../../shared";

// Código de la moneda en el web service del BCU, del que la tarea diaria trae
// el tipo de cambio. La moneda principal en pesos uruguayos lo deja vacío.
function campoCodigoBcu(respuesta) {
  return {
    nombre: "codigoBcu",
    tipoitemformularioid: 5,
    etiqueta: "Código BCU",
    opciones: "admin/config/moneda/bcu",
    respuesta,
    sinOpcionInicial: true,
    opcionVacia: true,
  };
}

export default function TiposMoneda() {
  const conCotizacionBcu = useFuncionalidad("COTIZACION_BCU");
  const [loader, setLoader] = useState(true);
  const [monedas, setMonedas] = useState([]);
  const [sideBarNueva, setSideBarNueva] = useState(false);
  const [loaderNueva, setLoaderNueva] = useState(false);
  const [formNuevaKey, setFormNuevaKey] = useState(0);
  const [sideBarEditar, setSideBarEditar] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [formEditarKey, setFormEditarKey] = useState(0);
  const [selectedMoneda, setSelectedMoneda] = useState(null);

  const toast = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    accessAPI(
      "GET",
      "admin/config/moneda",
      null,
      (respuesta) => {
        setMonedas(respuesta);
        setLoader(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar monedas",
          sticky: true,
        });
        setLoader(false);
      }
    );
  }, []);

  function crearMoneda(contenidoCampos) {
    setLoaderNueva(true);
    accessAPI(
      "POST",
      "admin/config/moneda",
      contenidoCampos,
      (respuesta) => {
        setMonedas((prev) => [...prev, respuesta]);
        setSideBarNueva(false);
        setLoaderNueva(false);
      },
      (respuesta) => {
        setLoaderNueva(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al crear moneda",
          sticky: true,
        });
      }
    );
  }

  function guardarEdicion(contenidoCampos) {
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/config/moneda/${selectedMoneda.id}`,
      contenidoCampos,
      (respuesta) => {
        setMonedas((prev) =>
          prev.map((m) => (m.id === selectedMoneda.id ? respuesta : m))
        );
        setSideBarEditar(false);
        setLoaderEditar(false);
      },
      (respuesta) => {
        setLoaderEditar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al guardar",
          sticky: true,
        });
      }
    );
  }

  // Pasa a ser la principal y la anterior deja de serlo; la API devuelve la
  // lista completa ya actualizada.
  function marcarPrincipal() {
    accessAPI(
      "PUT",
      `admin/config/moneda/${selectedMoneda.id}/principal`,
      null,
      (respuesta) => setMonedas(respuesta),
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al marcar la moneda como principal",
          sticky: true,
        });
      }
    );
  }

  function eliminarMoneda() {
    accessAPI(
      "DELETE",
      `admin/config/moneda/${selectedMoneda.id}`,
      null,
      () => {
        setMonedas((prev) => prev.filter((m) => m.id !== selectedMoneda.id));
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al eliminar",
          sticky: true,
        });
      }
    );
  }

  const menuItems = [
    ...(selectedMoneda && !selectedMoneda.principal
      ? [
          {
            label: "Marcar como principal",
            icon: "pi pi-star",
            command: marcarPrincipal,
          },
        ]
      : []),
    {
      label: "Editar",
      icon: "pi pi-pencil",
      command: () => {
        setFormEditarKey((k) => k + 1);
        setSideBarEditar(true);
      },
    },
    {
      label: "Eliminar",
      icon: "pi pi-trash",
      // La principal no se elimina: primero hay que marcar otra.
      disabled: Boolean(selectedMoneda?.principal),
      command: () => {
        confirmDialog({
          message: `¿Eliminar la moneda "${selectedMoneda?.nombre}"?`,
          header: "Confirmar eliminación",
          icon: "pi pi-exclamation-triangle",
          acceptLabel: "Eliminar",
          rejectLabel: "Cancelar",
          acceptClassName: "p-button-danger",
          accept: eliminarMoneda,
        });
      },
    },
  ];

  function getNuevaFormConfig() {
    return {
      secciones: [
        {
          campos: [
            {
              nombre: "nombre",
              tipoitemformularioid: 1,
              etiqueta: "Nombre",
              requerido: true,
            },
            {
              nombre: "simbolo",
              tipoitemformularioid: 1,
              etiqueta: "Símbolo",
              requerido: true,
            },
            {
              nombre: "codigo",
              tipoitemformularioid: 1,
              etiqueta: "Código",
            },
            ...(conCotizacionBcu ? [campoCodigoBcu(null)] : []),
          ],
        },
      ],
      botones: [
        {
          etiqueta: "Crear moneda",
          callback: crearMoneda,
          validar: true,
        },
      ],
    };
  }

  function getEditarFormConfig() {
    if (!selectedMoneda) return { secciones: [], botones: [] };
    return {
      secciones: [
        {
          campos: [
            {
              nombre: "nombre",
              tipoitemformularioid: 1,
              etiqueta: "Nombre",
              requerido: true,
              respuesta: selectedMoneda.nombre ?? "",
            },
            {
              nombre: "simbolo",
              tipoitemformularioid: 1,
              etiqueta: "Símbolo",
              requerido: true,
              respuesta: selectedMoneda.simbolo ?? "",
            },
            {
              nombre: "codigo",
              tipoitemformularioid: 1,
              etiqueta: "Código",
              respuesta: selectedMoneda.codigo ?? "",
            },
            ...(conCotizacionBcu ? [campoCodigoBcu(selectedMoneda.codigoBcu)] : []),
          ],
        },
      ],
      botones: [
        {
          etiqueta: "Guardar cambios",
          callback: guardarEdicion,
          validar: true,
        },
      ],
    };
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      <Menu model={menuItems} popup ref={menuRef} />

      <Sidebar
        visible={sideBarNueva}
        onHide={() => setSideBarNueva(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Nueva moneda</Titulo>
        <Formulario
          key={formNuevaKey}
          config={getNuevaFormConfig()}
          loader={loaderNueva}
        />
      </Sidebar>

      <Sidebar
        visible={sideBarEditar}
        onHide={() => setSideBarEditar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Editar moneda</Titulo>
        {selectedMoneda && (
          <Formulario
            key={formEditarKey}
            config={getEditarFormConfig()}
            loader={loaderEditar}
          />
        )}
      </Sidebar>

      {loader && (
        <Card>
          <Skeleton width="100%" height="3rem" />
          {[...Array(2)].map((_, i) => (
            <Skeleton
              key={i}
              width="100%"
              height="3.5rem"
              css={{ marginTop: "0.3rem" }}
            />
          ))}
        </Card>
      )}

      {!loader && (
        <Card>
          <Titulo
            botones={
              <Boton
                texto
                negro
                callback={() => {
                  setFormNuevaKey((k) => k + 1);
                  setSideBarNueva(true);
                }}
              >
                <i className="pi pi-plus" />
              </Boton>
            }
          >
            Tipos de moneda
          </Titulo>
          <TablaFiltrable conFiltros={false}
            value={monedas}
            stripedRows
            emptyMessage="No hay tipos de moneda ingresados"
            nombreArchivo="tipos_moneda"
          >
            <Column header="Nombre" field="nombre" />
            <Column header="Símbolo" field="simbolo" />
            <Column header="Código" field="codigo" />
            {conCotizacionBcu && <Column header="Código BCU" field="codigoBcu" />}
            {conCotizacionBcu && (
              <Column
                header="Tipo de cambio"
                body={(m) =>
                  m.tipoCambio != null
                    ? `${m.tipoCambio.toLocaleString("es-UY", { maximumFractionDigits: 6 })} (${new Date(
                        m.tipoCambioFecha
                      ).toLocaleDateString("es-UY", { timeZone: "UTC" })})`
                    : null
                }
                exportValue={(m) => m.tipoCambio ?? ""}
              />
            )}
            <Column
              header="Principal"
              body={(m) => (m.principal ? <Tag value="Principal" /> : null)}
              exportValue={(m) => (m.principal ? "Sí" : "No")}
            />
            <Column
              body={(rowData) => (
                <Boton
                  texto
                  negro
                  chico
                  callback={(e) => {
                    setSelectedMoneda(rowData);
                    menuRef.current.toggle(e);
                  }}
                >
                  <i className="pi pi-ellipsis-v" />
                </Boton>
              )}
              style={{ width: "60px", textAlign: "center" }}
            />
          </TablaFiltrable>
        </Card>
      )}
    </>
  );
}
